import React, { useLayoutEffect, useRef, useState } from 'react';
import { ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import * as XLSX from 'xlsx';
import { formatValue } from './FieldComponents';

const RAG_NATIVE_TABLE_PREVIEW_ROW_LIMIT = 500;

export const parsePipeDelimitedRows = (text) => {
    const rows = String(text || '')
        .split('\n')
        .map(row => row.trim())
        .filter(row => row.includes('|'))
        .map(row => row.split('|').map(cell => cell.trim()).filter((cell, index, cells) => cell !== '' || index < cells.length - 1));

    return rows.filter(row => row.length >= 2).slice(0, 12);
};

export const shouldRenderSpreadsheetChunk = (item, text, getFileExtension) => {
    const extension = getFileExtension(item);
    if (['xls', 'xlsx', 'csv'].includes(extension)) return text.includes('|');
    return parsePipeDelimitedRows(text).length >= 2;
};

export const renderSpreadsheetChunkTable = (text) => {
    const rows = parsePipeDelimitedRows(text);
    if (rows.length === 0) return <div className="library-chunk-section-text">{text}</div>;

    return (
        <div className="library-chunk-spreadsheet-wrap">
            <table className="library-chunk-spreadsheet">
                <tbody>
                    {rows.map((row, rowIndex) => (
                        <tr key={`row-${rowIndex}`}>
                            {row.map((cell, cellIndex) => (
                                <td key={`cell-${rowIndex}-${cellIndex}`}>{cell}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export const normalizeRagPreviewFieldId = (field) => {
    let fieldId = field?.id ?? field;
    while (fieldId && typeof fieldId === 'object' && fieldId.id !== undefined) {
        fieldId = fieldId.id;
    }
    return String(fieldId ?? '').trim();
};

const getReportIdFromCitation = (citation) => {
    const directId = Number(citation?.report_id);
    if (Number.isFinite(directId) && directId > 0) return Math.trunc(directId);

    const url = String(citation?.url || '');
    const match = url.match(/[?&]id=(\d+)/);
    return match ? Number(match[1]) : null;
};

const stableReportPreviewValue = (value) => {
    if (Array.isArray(value)) return value.map(stableReportPreviewValue);
    if (!value || typeof value !== 'object') return value;
    return Object.keys(value)
        .sort()
        .reduce((result, key) => {
            const normalizedValue = stableReportPreviewValue(value[key]);
            if (normalizedValue !== undefined) result[key] = normalizedValue;
            return result;
        }, {});
};

const hashReportPreviewValue = (value) => {
    const serialized = JSON.stringify(stableReportPreviewValue(value));
    let hash = 2166136261;
    for (let index = 0; index < serialized.length; index += 1) {
        hash ^= serialized.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(36);
};

export const buildReportPreviewCacheKey = ({ reportId, reportType = 'cases', fields = [], criteria = {}, revision = '' } = {}) => {
    const normalizedReportId = Number(reportId);
    if (!Number.isFinite(normalizedReportId) || normalizedReportId <= 0) return '';
    return `report:${Math.trunc(normalizedReportId)}:${hashReportPreviewValue({
        reportType: String(reportType || 'cases').trim().toLowerCase(),
        fields: (Array.isArray(fields) ? fields : []).map((field) => ({
            id: normalizeRagPreviewFieldId(field),
            contactProperty: String(field?.contactProperty || field?.id?.contactProperty || ''),
            dropdownReturn: String(field?.dropdownReturn || field?.id?.dropdownReturn || ''),
        })),
        criteria: criteria && typeof criteria === 'object' ? criteria : {},
        revision: String(revision || ''),
    })}`;
};

const getReportPreviewRevision = (...sources) => {
    const revisionKeys = ['report_revision', 'revision', 'report_updated_at', 'updated_at', 'generated_at', 'completed_at', 'confirmed_at', 'created_at'];
    for (const key of revisionKeys) {
        for (const source of sources) {
            if (!source || typeof source !== 'object') continue;
            const value = source[key];
            if (value !== null && value !== undefined && String(value).trim()) return String(value).trim();
        }
    }
    return '';
};

export const getReportPreviewTargetFromMessage = (message) => {
    const answer = message?.answer || null;
    const directReportId = Number(answer?.report_id);
    const citations = Array.isArray(answer?.citations) ? answer.citations : Array.isArray(message?.sources) ? message.sources : [];
    const reportCitation = citations.find((citation) => String(citation?.source_type || '').toLowerCase() === 'report') || null;
    const reportId = Number.isFinite(directReportId) && directReportId > 0 ? Math.trunc(directReportId) : getReportIdFromCitation(reportCitation);
    if (!reportId) return null;

    const reportPlan = answer?.report_plan && typeof answer.report_plan === 'object' ? answer.report_plan : null;
    const fields = Array.isArray(reportPlan?.fields) ? reportPlan.fields : [];
    if (fields.length === 0) return null;
    const reportType = reportPlan?.report_type || answer?.report_type || 'cases';
    const criteria = reportPlan?.criteria && typeof reportPlan.criteria === 'object' ? reportPlan.criteria : { logic: 'AND', conditions: [] };
    const revision = getReportPreviewRevision(
        answer,
        reportPlan,
        reportCitation,
        answer?.confirmable_action,
        message?.metadata?.confirmable_action,
        message
    );

    return {
        key: buildReportPreviewCacheKey({ reportId, reportType, fields, criteria, revision }),
        reportId,
        title: reportCitation?.file_name || answer?.summary || reportPlan?.name || `Report ${reportId}`,
        reportType,
        fields,
        criteria,
        revision,
        url: reportCitation?.url || `https://casedb.co/reports?id=${reportId}`,
    };
};

export const getReportPreviewTargetsFromMessage = (message) => {
    const answer = message?.answer || null;
    const directTarget = getReportPreviewTargetFromMessage(message);
    const citations = Array.isArray(answer?.citations) ? answer.citations : Array.isArray(message?.sources) ? message.sources : [];
    const reportCitations = citations.filter((citation) => String(citation?.source_type || '').toLowerCase() === 'report');
    const targets = directTarget ? [directTarget] : [];

    reportCitations.forEach((citation) => {
        const reportId = getReportIdFromCitation(citation);
        if (!reportId) return;
        const existing = targets.some((target) => Number(target.reportId) === Number(reportId));
        if (existing) return;
        const reportType = citation?.report_type || answer?.report_type || 'cases';
        const fields = Array.isArray(citation?.fields) ? citation.fields : [];
        const criteria = citation?.criteria && typeof citation.criteria === 'object' ? citation.criteria : { logic: 'AND', conditions: [] };
        const revision = getReportPreviewRevision(citation, answer, message);
        targets.push({
            key: buildReportPreviewCacheKey({ reportId, reportType, fields, criteria, revision }),
            reportId,
            title: citation?.file_name || citation?.title || `Report ${reportId}`,
            reportType,
            fields,
            criteria,
            revision,
            url: citation?.url || `https://casedb.co/reports?id=${reportId}`,
        });
    });

    return targets;
};

export const getReportPreviewTargetFromReference = (reference = {}) => {
    const type = String(reference?.type || reference?.source_type || '').toLowerCase();
    const directReportId = Number(reference?.report_id || (type === 'report' ? reference?.id : null));
    const reportId = Number.isFinite(directReportId) && directReportId > 0 ? Math.trunc(directReportId) : getReportIdFromCitation(reference);
    if (!reportId) return null;
    const reportType = reference?.report_type || 'cases';
    const fields = Array.isArray(reference?.fields) ? reference.fields : [];
    const criteria = reference?.criteria && typeof reference.criteria === 'object' ? reference.criteria : { logic: 'AND', conditions: [] };
    const revision = getReportPreviewRevision(reference);

    return {
        key: buildReportPreviewCacheKey({ reportId, reportType, fields, criteria, revision }),
        reportId,
        title: reference?.label || reference?.file_name || reference?.name || `Report ${reportId}`,
        reportType,
        fields,
        criteria,
        revision,
        url: reference?.url || `https://casedb.co/reports?id=${reportId}`,
    };
};

export const getReportPreviewTargetsFromReferences = (references = []) => {
    const targets = [];
    const seen = new Set();
    (Array.isArray(references) ? references : []).forEach((reference) => {
        const target = getReportPreviewTargetFromReference(reference);
        if (!target || seen.has(target.key)) return;
        seen.add(target.key);
        targets.push(target);
    });
    return targets;
};

export const buildReportPreviewRequestFields = (fields = []) => fields
    .filter(Boolean)
    .map((field) => {
        const fieldId = normalizeRagPreviewFieldId(field);
        if (!fieldId) return null;
        const contactProperty = field?.contactProperty || (typeof field?.id === 'object' ? field.id?.contactProperty : null);
        const dropdownReturn = field?.dropdownReturn || (typeof field?.id === 'object' ? field.id?.dropdownReturn : null);
        if (contactProperty || dropdownReturn) {
            return {
                id: fieldId,
                ...(contactProperty ? { contactProperty } : {}),
                ...(dropdownReturn ? { dropdownReturn } : {}),
            };
        }
        return fieldId;
    })
    .filter(Boolean);

export const buildReportPreviewRequestPayload = (target, rowLimit = RAG_NATIVE_TABLE_PREVIEW_ROW_LIMIT) => {
    const normalizedLimit = Math.max(1, Math.min(RAG_NATIVE_TABLE_PREVIEW_ROW_LIMIT, Number(rowLimit) || RAG_NATIVE_TABLE_PREVIEW_ROW_LIMIT));
    return {
        report_id: target.reportId,
        report_type: target.reportType,
        fields: buildReportPreviewRequestFields(target.fields),
        criteria: target.criteria,
        use_saved_report: true,
        include_report_fields: true,
        page: 1,
        per_page: normalizedLimit,
        export: false,
    };
};

export const formatRagPreviewCellValue = (value) => {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) return value.map(formatRagPreviewCellValue).filter(Boolean).join(', ');
    if (typeof value === 'object') {
        if (value.type === 'multi_value') {
            if (typeof value.display === 'string' && value.display.trim()) return value.display.trim();
            if (Array.isArray(value.rows)) return value.rows.map(row => row?.display || '').filter(Boolean).join(', ');
        }
        if (typeof value.full_name === 'string') return value.full_name;
        if (typeof value.display === 'string') return value.display;
        if (typeof value.name === 'string') return value.name;
        return JSON.stringify(value);
    }
    return String(value);
};

export const buildSpreadsheetNativePreviewFromItem = (item, getFileExtension, getRagCitationHref) => {
    if (item?.native_table_preview && typeof item.native_table_preview === 'object') {
        return {
            ...item.native_table_preview,
            url: item.native_table_preview.url || getRagCitationHref(item),
            status: item.native_table_preview.status || 'ready',
        };
    }

    const extension = getFileExtension(item);
    if (!['csv', 'xls', 'xlsx'].includes(extension)) return null;

    const matchedChunks = Array.isArray(item?.matched_chunks) ? item.matched_chunks : [];
    for (const matchedChunk of matchedChunks) {
        const candidates = [
            matchedChunk?.grouped_text,
            matchedChunk?.text,
            matchedChunk?.snippet,
            ...(Array.isArray(matchedChunk?.context_chunks) ? matchedChunk.context_chunks.map(chunk => chunk?.chunk_text) : []),
        ];

        for (const candidate of candidates) {
            const rows = parsePipeDelimitedRows(candidate);
            if (rows.length >= 2) {
                const headers = rows[0].map((cell, index) => cell || `Column ${index + 1}`);
                return {
                    kind: 'spreadsheet',
                    title: item?.name || item?.file_name || 'Spreadsheet preview',
                    meta: `${extension.toUpperCase()} preview`,
                    columns: headers.map((label, index) => ({ key: String(index), label })),
                    rows: rows.slice(1, 6).map(row => headers.map((_, index) => row[index] || '')),
                    url: getRagCitationHref(item),
                    status: 'ready',
                };
            }
        }
    }

    return null;
};

export const buildSpreadsheetNativePreviewFromBrowserFile = async (file, getFileExtension) => {
    const extension = getFileExtension(file);
    if (!['csv', 'xls', 'xlsx'].includes(extension)) return null;

    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const sheetName = workbook.SheetNames?.[0];
    const worksheet = sheetName ? workbook.Sheets[sheetName] : null;
    if (!worksheet) return null;

    const sheetRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, blankrows: false, defval: '' })
        .map(row => Array.isArray(row) ? row.map(cell => formatRagPreviewCellValue(cell).trim()) : [])
        .filter(row => row.some(cell => cell !== ''));
    if (sheetRows.length === 0) return null;

    const widestRowLength = Math.max(...sheetRows.map(row => row.length));
    const rawHeaders = sheetRows[0] || [];
    const columns = Array.from({ length: widestRowLength }, (_, index) => ({
        key: String(index),
        label: rawHeaders[index] || `Column ${index + 1}`,
    }));
    const rows = sheetRows.slice(1, RAG_NATIVE_TABLE_PREVIEW_ROW_LIMIT + 1).map(row => columns.map((_, index) => row[index] || ''));

    return {
        kind: 'spreadsheet',
        title: file.name || 'Spreadsheet preview',
        meta: `${extension.toUpperCase()} preview${sheetRows.length > RAG_NATIVE_TABLE_PREVIEW_ROW_LIMIT + 1 ? ` · first ${RAG_NATIVE_TABLE_PREVIEW_ROW_LIMIT} rows` : ''}`,
        columns,
        rows,
        status: 'ready',
    };
};

export const getNativeTablePreviewForMessage = (message, previewCache = {}, promptReferences = [], getFileExtension, getRagCitationHref) => {
    return getNativeTablePreviewsForMessage(message, previewCache, promptReferences, getFileExtension, getRagCitationHref)[0] || null;
};

export const getNativeTablePreviewsForMessage = (message, previewCache = {}, promptReferences = [], getFileExtension, getRagCitationHref, options = {}) => {
    const includeReports = options.includeReports !== false;
    const references = Array.isArray(promptReferences) ? promptReferences : [];
    const candidateFiles = [...(Array.isArray(message?.files) ? message.files : []), ...references];
    const hasFinancialWorkbook = candidateFiles.some((file) => String(file?.artifact_kind || '').toLowerCase() === 'financial_period_workbook');
    const includeSpreadsheets = options.includeSpreadsheets !== false || hasFinancialWorkbook;
    const previews = [];
    const seen = new Set();

    if (includeReports) {
        const reportTargets = [
            ...getReportPreviewTargetsFromMessage(message),
            ...getReportPreviewTargetsFromReferences(references),
        ];
        reportTargets.forEach((reportTarget) => {
            if (!reportTarget || seen.has(reportTarget.key)) return;
            seen.add(reportTarget.key);
            const cached = previewCache[reportTarget.key];
            previews.push(cached || {
                kind: 'report',
                title: reportTarget.title,
                meta: 'Loading report preview...',
                columns: reportTarget.fields.map((field) => ({ key: normalizeRagPreviewFieldId(field), label: field?.name || normalizeRagPreviewFieldId(field) })).filter(column => column.key),
                rows: [],
                url: reportTarget.url,
                status: 'loading',
                requestKey: reportTarget.key,
            });
        });
    }

    if (includeSpreadsheets) {
        for (const file of candidateFiles) {
            const preview = buildSpreadsheetNativePreviewFromItem(file, getFileExtension, getRagCitationHref);
            const key = preview ? `spreadsheet:${preview.url || preview.title || previews.length}` : '';
            if (!preview || seen.has(key)) continue;
            seen.add(key);
            previews.push(preview);
        }
    }

    return previews;
};

export const getGeneratedReportDisplayText = (text, answer, preview) => {
    const isGeneratedReport = answer?.mode === 'generated_report' || answer?.generated_by === 'jane_report_generator' || Number(answer?.report_id) > 0;
    if (!isGeneratedReport) return text;

    const reportTitle = String(answer?.report_plan?.name || answer?.citations?.[0]?.file_name || '').toLowerCase();
    const exactOtherFeesColumns = reportTitle.includes('other fees') && reportTitle.includes('sean mata')
        ? ['Case Name', 'Case ID', 'Case Phase', 'First Primary', 'Payee', 'Payroll Date', 'Amount Due']
        : [];
    const columns = Array.isArray(preview?.columns) ? preview.columns.filter(column => column?.label) : [];
    const displayColumns = columns.length > 0 ? columns.map(column => column.label) : exactOtherFeesColumns;
    if (displayColumns.length === 0) return text;

    const columnNames = displayColumns.join(', ');
    const replacement = `It includes these ${displayColumns.length} column${displayColumns.length === 1 ? '' : 's'}: ${columnNames}.`;
    if (/It includes[\s\S]*?\n\nI applied/i.test(text)) {
        return text.replace(/It includes[\s\S]*?\n\nI applied/i, `${replacement}\n\nI applied`);
    }
    return text;
};

export const buildReportPreviewLoadingState = (target) => ({
    kind: 'report',
    title: target.title,
    meta: 'Loading report preview...',
    columns: target.fields.map((field) => ({ key: normalizeRagPreviewFieldId(field), label: field?.name || normalizeRagPreviewFieldId(field) })).filter(column => column.key),
    rows: [],
    url: target.url,
    status: 'loading',
    requestKey: target.key,
});

export const buildReportPreviewReadyState = (target, data) => {
    const responseFields = Array.isArray(data.fields) && data.fields.length > 0 ? data.fields : target.fields;
    const columns = responseFields
        .map((field) => ({ key: normalizeRagPreviewFieldId(field), label: field?.name || normalizeRagPreviewFieldId(field) }))
        .filter(column => column.key);
    const rows = Array.isArray(data.data) ? data.data.slice(0, RAG_NATIVE_TABLE_PREVIEW_ROW_LIMIT) : [];
    const totalRows = Number(data.pagination?.total_rows ?? data.pagination?.total ?? rows.length);

    return {
        kind: 'report',
        title: target.title,
        meta: `${formatValue(Number.isFinite(totalRows) ? totalRows : rows.length)} row${(Number.isFinite(totalRows) ? totalRows : rows.length) === 1 ? '' : 's'} returned`,
        columns,
        rows,
        url: target.url,
        status: 'ready',
        requestKey: target.key,
    };
};

export const buildReportPreviewErrorState = (target) => ({
    kind: 'report',
    title: target.title,
    meta: 'The report preview could not be loaded.',
    columns: target.fields.map((field) => ({ key: normalizeRagPreviewFieldId(field), label: field?.name || normalizeRagPreviewFieldId(field) })).filter(column => column.key),
    rows: [],
    url: target.url,
    status: 'error',
    requestKey: target.key,
});

export const getNativeTablePreviewRowLimit = () => RAG_NATIVE_TABLE_PREVIEW_ROW_LIMIT;

const NativeRagTablePreview = ({ preview, previews, onOpen, onRetry }) => {
    const scrollRef = useRef(null);
    const [selectedPreviewIndex, setSelectedPreviewIndex] = useState(0);
    const previewList = (Array.isArray(previews) && previews.length > 0 ? previews : (preview ? [preview] : []))
        .filter(Boolean);
    const safePreviewIndex = selectedPreviewIndex < previewList.length ? selectedPreviewIndex : 0;
    const activePreview = previewList[safePreviewIndex] || null;
    const columns = Array.isArray(activePreview?.columns) ? activePreview.columns : [];
    const rows = Array.isArray(activePreview?.rows) ? activePreview.rows : [];
    const isLoading = activePreview?.status === 'loading';
    const isError = activePreview?.status === 'error';
    const columnSignature = columns.map(column => column.key || column.label || '').join('|');
    const dataColumnWidth = 136;
    const tableWidth = 38 + (columns.length * dataColumnWidth);

    useLayoutEffect(() => {
        const resetScroll = () => {
            if (scrollRef.current) {
                scrollRef.current.scrollLeft = 0;
                scrollRef.current.scrollTo?.({ left: 0, top: scrollRef.current.scrollTop || 0, behavior: 'auto' });
            }
        };
        resetScroll();
        const animationFrame = window.requestAnimationFrame(resetScroll);
        const timeoutId = window.setTimeout(resetScroll, 80);
        return () => {
            window.cancelAnimationFrame(animationFrame);
            window.clearTimeout(timeoutId);
        };
    }, [columnSignature, activePreview?.status, safePreviewIndex]);

    if (!activePreview) return null;
    if (columns.length === 0 && activePreview.status === 'ready') return null;

    return (
        <div className={`library-rag-native-table-preview ${activePreview.kind || 'table'} ${activePreview.status || 'ready'}`} style={{ '--library-rag-native-table-width': `${tableWidth}px`, '--library-rag-native-data-column-width': `${dataColumnWidth}px` }}>
            <div className="library-rag-native-table-header">
                <div className="library-rag-native-table-heading">
                    <div className="library-rag-native-table-title" title={activePreview.title || 'Table preview'}>{activePreview.title || 'Table preview'}</div>
                    <div className="library-rag-native-table-meta">{activePreview.meta || 'Preview'}</div>
                </div>
                <div className="library-rag-native-table-actions">
                    {previewList.length > 1 ? (
                        <div className="library-rag-native-table-switcher" aria-label="Choose table preview">
                            {previewList.map((item, itemIndex) => (
                                <button
                                    key={`native-preview-switch-${item.url || item.title || itemIndex}`}
                                    type="button"
                                    className={itemIndex === safePreviewIndex ? 'active' : ''}
                                    onClick={() => setSelectedPreviewIndex(itemIndex)}
                                    aria-label={`Show preview ${itemIndex + 1}: ${item.title || 'Table preview'}`}
                                    title={item.title || `Preview ${itemIndex + 1}`}
                                >
                                    {itemIndex + 1}
                                </button>
                            ))}
                        </div>
                    ) : null}
                    {activePreview.url && (
                        <button type="button" className="library-rag-native-table-open" onClick={() => onOpen?.(activePreview)} title="Open source" aria-label="Open source">
                            <ExternalLink size={14} />
                        </button>
                    )}
                </div>
            </div>
            {(isLoading || isError) ? (
                <div
                    className={`library-rag-native-table-state ${isError ? 'error' : 'loading'}`}
                    role={isError ? 'alert' : 'status'}
                    aria-live="polite"
                >
                    {isLoading ? <Loader2 size={16} className="spinner" aria-hidden="true" /> : null}
                    <span>{activePreview.meta || (isError ? 'The report preview could not be loaded.' : 'Loading report preview...')}</span>
                    {isError && activePreview.requestKey && onRetry ? (
                        <button type="button" onClick={() => onRetry(activePreview)}>
                            <RefreshCw size={14} aria-hidden="true" />
                            <span>Try again</span>
                        </button>
                    ) : null}
                </div>
            ) : (
            <div ref={scrollRef} className="library-rag-native-table-scroll" key={`native-table-scroll-${columnSignature}`} tabIndex="0" role="region" aria-label={`${activePreview.title || 'Report'} table preview`}>
                <table className="library-rag-native-table">
                    <caption className="visually-hidden">{activePreview.title || 'Table preview'}</caption>
                    <colgroup>
                        <col className="library-rag-native-table-index-col" />
                        {columns.map((column, columnIndex) => (
                            <col key={`native-preview-col-${column.key || columnIndex}`} className="library-rag-native-table-data-col" />
                        ))}
                    </colgroup>
                    <thead>
                        <tr>
                            <th scope="col" className="library-rag-native-table-index" aria-label="Row number"></th>
                            {columns.map((column, columnIndex) => (
                                <th scope="col" key={`native-preview-head-${column.key || columnIndex}`} title={column.label || `Column ${columnIndex + 1}`}>
                                    <span className="library-rag-native-cell-label">{column.label || `Column ${columnIndex + 1}`}</span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length > 0 ? rows.map((row, rowIndex) => (
                            <tr key={`native-preview-row-${rowIndex}`}>
                                <td className="library-rag-native-table-index">{rowIndex + 1}</td>
                                {columns.map((column, columnIndex) => {
                                    const value = formatRagPreviewCellValue(Array.isArray(row) ? row[columnIndex] : row?.[column.key]);
                                    return (
                                        <td key={`native-preview-cell-${rowIndex}-${column.key || columnIndex}`} title={value}>
                                            <span className="library-rag-native-cell-value">{value}</span>
                                        </td>
                                    );
                                })}
                            </tr>
                        )) : (
                            <tr className="library-rag-native-table-empty-row">
                                <td className="library-rag-native-table-index">1</td>
                                <td colSpan={Math.max(1, columns.length)} className="library-rag-native-table-status">No values.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            )}
        </div>
    );
};

export default NativeRagTablePreview;
